/**
 * Default Nuxt router.options.js copied from nuxt/dist/pages/runtime/router.options.js
 */
import { useNuxtApp } from 'nuxt/app'
import { useRouter } from 'nuxt/app'
import { logger } from '../utility/logger'

// NOTE: This is a hardcoded fallback for the default page transition.
// It was previously imported from the build context to stay in sync with nuxt.config.ts.
// If you change the default `app.pageTransition` in nuxt.config.ts,
// you MUST update this value to match.
const defaultPageTransition = { name: 'fade', mode: 'out-in' }

// Helper function to check if the page is changing
function isChangingPage(to: any, from: any): boolean {
  if (!from || !to) return false
  if (to === from) return false
  return to.name !== from.name || to.path !== from.path
}

export default {
  scrollBehavior(to, from, savedPosition) {
    const nuxtApp = useNuxtApp()
    const behavior = 'auto'
    let position = savedPosition || 0
    const routeAllowsScrollToTop =
      typeof to.meta.scrollToTop === 'function'
        ? to.meta.scrollToTop(to, from)
        : to.meta.scrollToTop
    if (
      !position &&
      from &&
      to &&
      routeAllowsScrollToTop !== false &&
      isChangingPage(to, from)
    ) {
      position = { left: 0, top: 0 }
    }
    if (to.path === from.path) {
      // Commenting out as we wan't to keep scroll position when closing modals that use hash
      // shouldn't cause an issue for us where we want to scrol to top using a link without a hash
      // if (from.hash && !to.hash) {
      //   return { left: 0, top: 0 }
      // }
      if (to.hash) {
        logger('vue router scroll', to.hash)
        return {
          el: to.hash,
          top: _getHashElementScrollMarginTop(to.hash),
          behavior,
        }
      }
      return false
    }

    // For page changes, scroll immediately to top if needed
    // Don't wait for transitions when changing pages
    if (
      isChangingPage(to, from) &&
      routeAllowsScrollToTop !== false &&
      !to.hash
    ) {
      // Scroll immediately for better UX
      return position
    }

    const hasTransition = (route) =>
      !!(route.meta.pageTransition ?? defaultPageTransition)
    const hookToWait =
      hasTransition(from) && hasTransition(to)
        ? 'page:transition:finish'
        : 'page:finish'
    return new Promise((resolve) => {
      nuxtApp.hooks.hookOnce(hookToWait, async () => {
        await new Promise((resolve2) => setTimeout(resolve2, 0))
        if (to.hash) {
          position = {
            el: to.hash,
            top: _getHashElementScrollMarginTop(to.hash),
            behavior,
          }
        }
        logger('vue router scroll', to.hash)
        resolve(position)
      })
    })
  },
}
function _getHashElementScrollMarginTop(selector) {
  try {
    const elem = document.querySelector(selector)
    if (elem) {
      return (
        (Number.parseFloat(getComputedStyle(elem).scrollMarginTop) || 0) +
        (Number.parseFloat(
          getComputedStyle(document.documentElement).scrollPaddingTop,
        ) || 0)
      )
    }
  } catch {}
  return 0
}
